import React, { useCallback } from 'react';

import { regionsWithMetroEnumeration } from '@search/filter-enums/enums/Region';
import { Rooms } from '@search/filter-enums/enums/Rooms';

import {
    Filters, GeoFilter, IFilter, MapBoundsFilter, RegionFilter, TransportLimitFilter,
    KitchenAreaFilter, RoomFilter
} from '../../../view/filters/models/Filter';
import { TipItemBase, getTipsCollection } from '../../../view/tips/OffersSearchTips';
import { GeoBaseStore } from '../../geo/GeoBase';
import { QueryDisplayValues } from '../../../view/common/hooks/useQueryDisplayValues';
import { useKeyCancel } from '../../../view/common/hooks/useKey';
import { ClassifiedFilterCollection } from './ClassifiedFilterCollection';
import { classifiedFilterFactory } from './ClassifiedFilterFactory';

export function useOfferFiltersDesktop({
    updateFilters,
    filters,
    geoStore,
    queryDisplayValues
}: {
    queryDisplayValues: QueryDisplayValues;
    geoStore: GeoBaseStore;
    updateFilters: (next: ClassifiedFilterCollection) => void;
    filters: ClassifiedFilterCollection;
}) {
    const closeDrawAreaRef = React.useRef(() => {});

    const handleReset = useCallback(() => {
        const nextFilters = classifiedFilterFactory.clearAdditionalFilters(filters.getFilters());
        const filterCollection = classifiedFilterFactory.create(nextFilters);

        filterCollection.update(new MapBoundsFilter());

        updateFilters(filterCollection);
        closeDrawAreaRef.current();
    }, [ filters, updateFilters ]);

    const handleResetNoGeo = useCallback(() => {
        const nextFilters = classifiedFilterFactory.clearAdditionalFilters(filters.getFilters(), [ Filters.geo ]);
        const filterCollection = classifiedFilterFactory.create(nextFilters);

        updateFilters(filterCollection);
    }, [ filters, updateFilters ]);

    const removeFilter = useCallback(
        (filter: IFilter) => {
            const newFilters = filters.remove(filter) as ClassifiedFilterCollection;

            if (filter.id === Filters.geo || filter.id === Filters.mapBounds) {
                filters.update(new MapBoundsFilter());
                closeDrawAreaRef.current();
            }

            updateFilters(newFilters);
        },
        [ filters, updateFilters ]
    );

    const updateFewFilters = useCallback((filtersForUpdate: IFilter[]) => {
        const updatedFilters = filtersForUpdate.reduce((acc, filter) => {
            let newFilters = acc.update(filter);

            if (filter.id === Filters.geo) {
                newFilters = acc.update(new MapBoundsFilter());
                closeDrawAreaRef.current();
            }

            return newFilters;
        }, filters);

        updateFilters(updatedFilters);
    }, [ filters, updateFilters ]);

    const updateFilter = useCallback((filter: IFilter) => {
        if (filter.id === Filters.region) {
            filters.update(new GeoFilter());
            filters.update(new MapBoundsFilter());
            closeDrawAreaRef.current();

            if (! regionsWithMetroEnumeration.has((filter as RegionFilter).region)) {
                filters.update(new TransportLimitFilter());
            }
        }

        if (filter.id === Filters.geo) {
            filters.update(new MapBoundsFilter());
            closeDrawAreaRef.current();
        }

        if (filter.id === Filters.rooms && (filter as RoomFilter).rooms.includes(Rooms.STUDIO)) {
            filters.update(new KitchenAreaFilter({ from: null, to: null }));
        }

        const newFilters: ClassifiedFilterCollection = classifiedFilterFactory
            .createOrUpdateFilters(filters.update(filter).getFilters(), filter);

        updateFilters(newFilters);
    }, [ filters, updateFilters ]);

    const filterGeo = filters.get(Filters.geo) as GeoFilter;

    const removeGeo = React.useCallback((tips: TipItemBase[]) => {
        let newFilters = filters;

        const geoIds = new Set<number>(filterGeo.geoIds);

        let metroIds = [ ...filterGeo.metroIds ];
        let districtIds = [ ...filterGeo.districtIds ];
        let addressIds = [ ...filterGeo.addressIds ];

        for (const tip of tips) {
            if (tip.__typename === 'TipRouteMetro') {
                newFilters = newFilters.remove(newFilters.get(Filters.transportLimit)) as ClassifiedFilterCollection;
                continue;
            }
            const id = tip.geoId ?? tip.id;

            geoIds.delete(id);
            metroIds = metroIds.filter(item => item !== id);
            districtIds = districtIds.filter(item => item !== id);
            addressIds = addressIds.filter(item => item !== id);
        }

        const newGeoFilter = new GeoFilter(
            geoIds,
            metroIds,
            districtIds,
            addressIds
        );

        const oldMapBounds = filters.get(Filters.mapBounds) as MapBoundsFilter;

        newFilters = newFilters
            .update(newGeoFilter)
            .update(
                new MapBoundsFilter(
                    oldMapBounds.bounds,
                    oldMapBounds.polygon,
                    oldMapBounds.geohash
                )
            ) as ClassifiedFilterCollection;

        updateFilters(newFilters);
        // closeDrawAreaRef.current();
    }, [ filters, updateFilters, filterGeo ]);

    const [ extraFilterVisible, setExtraFilterVisible ] = React.useState(false);
    const closeExtraVisible = React.useCallback(() => {
        setExtraFilterVisible(false);
    }, [ setExtraFilterVisible ]);
    const openExtraFilter = React.useCallback(() => {
        setExtraFilterVisible(true);
    }, [ setExtraFilterVisible ]);

    useKeyCancel(closeExtraVisible);

    const [ tipsExpanded, setTipsExpanded ] = React.useState(false);

    const tipsVisibleInitial = React.useMemo(
        () => getTipsCollection({
            geoStore,
            filters,
            queryDisplayValues
        }).tipsCollection.length > 0,
        [ geoStore, filters, queryDisplayValues ]);

    const [ tipsVisible, setTipsVisible ] = React.useState(tipsVisibleInitial);

    return {
        closeDrawAreaRef,

        extraFilterVisible,
        closeExtraVisible,
        openExtraFilter,

        tipsExpanded,
        setTipsExpanded,
        tipsVisible,
        setTipsVisible,
        handleReset,
        handleResetNoGeo,
        removeFilter,
        updateFewFilters,
        updateFilter,
        removeGeo
    };
}
